// src/components/Eventos/EventsHeats/EventHeats.jsx
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, useTheme,
  useMediaQuery, Alert, Button, Stack, Tooltip, Skeleton,
  Dialog, DialogContent, DialogActions, CircularProgress, Typography
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useEventContext } from '../../../context/EventContext';
import CustomAccordion from '../../CustomAccordion';

// Importar componentes refactorizados
import EventHeader from './components/EventHeader';
import EventHeatRow from './components/EventHeatRow';
import PhotofinishModal from './components/PhotofinishModal';
import AbbreviationsLegend from './components/AbbreviationsLegend';

// Importar hooks
import { useEventHeatsData } from './hooks/useEventHeatsData';
import { useAccordionState } from './hooks/useAccordionState';
import { usePhotofinish } from './hooks/usePhotofinish';

// Importar utilidades
import { getFontSize } from './utils/eventHelpers';

export default function EventHeats({ eventNumber: propEvt, round: propRnd }) {
  /* ───── router y contexto ───── */
  const params = useParams();
  const eventNumber = propEvt ?? params.eventNumber;
  const round = propRnd ?? params.round;
  const navigate = useNavigate();
  const theme = useTheme();
  
  const { competitionId, getHeatImages } = useEventContext();
  
  // Estados locales
  const [sortBy, setSortBy] = useState('place');
  const [filterTeam, setFilterTeam] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Hooks personalizados
  const {
    eventDetails,
    genderLabel,
    totalParts,
    isFieldEvent,
    isVerticalEvent,
    hasPlaceOrResult,
    uniqueTeams,
    processHeatRows,
    globalResults
  } = useEventHeatsData(eventNumber, round);
  
  const { openAccordions, handleAccordionToggle } = useAccordionState();
  
  const {
    photofinishModal,
    heatImagesStatus,
    handlePhotofinishClick,
    closePhotofinishModal
  } = usePhotofinish(eventDetails, getHeatImages);
  
  // Responsive
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Funciones de navegación
  const goSet = (e, r, h) => navigate(`/${competitionId}/event/${e}/${r}/${h}/details`);

  /* ═════════════ RENDERIZADO CONDICIONAL TEMPRANO ═════════════ */
  if (!eventDetails.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert 
          severity="warning" 
          variant="outlined"
          sx={{ 
            borderRadius: 2,
            '& .MuiAlert-icon': {
              fontSize: '1.5rem'
            }
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            No se encontraron heats para este evento / ronda.
          </Typography>
          <Typography variant="body2">
            Verifica que hayas seleccionado el evento y ronda correctos.
          </Typography>
        </Alert>
      </Box>
    );
  }

  /* ───── Filtrar resultados globales ───── */
  const filteredGlobalRows = globalResults.rows.filter(r => 
    filterTeam ? r.team === filterTeam : true
  );

  /* ───── Crear las tablas de heats ───── */
  const heatsTables = eventDetails.map(heat => {
    const { rows, hasScores, hasYear, key } = processHeatRows(heat);

    const header = (
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ 
            fontSize: getFontSize(16, 1, isXs, isSm, isMd),
            fontWeight: 'medium'
          }}>
            Heat {heat.heat}
          </Typography>
          <Chip 
            label={`${rows.length} atletas`}
            size="small"
            variant="outlined"
            sx={{ 
              ml: 1, 
              fontSize: getFontSize(12, 1, isXs, isSm, isMd),
              height: 20,
              fontWeight: 'medium',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              color: theme.palette.primary.main,
              border: `1px solid ${theme.palette.primary.main}`,
            }}
          />
          {heatImagesStatus[heat.heat] && (
            <Chip 
              icon={<CameraAltIcon sx={{ 
                fontSize: '14px !important',
                color: 'inherit'
              }} />}
              label="Fotofinish"
              size="small"
              variant="filled"
              sx={{ 
                ml: 1, 
                fontSize: getFontSize(11, 1, isXs, isSm, isMd),
                height: 22,
                fontWeight: 'medium',
                borderRadius: '12px',
                backgroundColor: openAccordions.has(key) ? '#2196f3' : '#ffffff',
                color: openAccordions.has(key) ? 'white' : theme.palette.primary.main,
                boxShadow: openAccordions.has(key) 
                  ? '0 2px 8px rgba(33, 150, 243, 0.3)' 
                  : '0 1px 3px rgba(0,0,0,0.1)',
                '&:hover': {
                  backgroundColor: openAccordions.has(key) ? '#1976d2' : '#f0f7ff',
                  transform: 'translateY(-1px)',
                  boxShadow: openAccordions.has(key) 
                    ? '0 4px 12px rgba(33, 150, 243, 0.4)' 
                    : '0 2px 6px rgba(0,0,0,0.15)',
                }
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {heatImagesStatus[heat.heat] && (
            <Tooltip title="Ver fotofinish">
              <IconButton 
                size={isXs ? "small" : "medium"}
                onClick={(e) => handlePhotofinishClick(e, heat.heat, heat.eventNumber, heat.round)}
                sx={{
                  color: theme.palette.primary.main,
                  backgroundColor: 'transparent',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    transform: 'scale(1.1)',
                    color: theme.palette.primary.main
                  }
                }}
              >
                <CameraAltIcon fontSize={isXs ? "small" : "medium"} />
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title="Ajustes del heat">
            <IconButton 
              size={isXs ? "small" : "medium"}
              onClick={e => { e.stopPropagation(); goSet(heat.eventNumber, heat.round, heat.heat); }}
              color="primary"
              sx={{
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: theme.palette.primary.light,
                  transform: 'scale(1.1)',
                  opacity: 0.8
                }
              }}
            >
              <SettingsIcon fontSize={isXs ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    );

    return (
      <CustomAccordion 
        key={key} 
        headerComponent={header}
        defaultExpanded={false}
        onChange={(isExpanded) => handleAccordionToggle(key, isExpanded)}
      >
        <TableContainer 
          component={Paper}
          elevation={0}
          variant="outlined"
          sx={{ 
            overflow: 'auto',
            borderRadius: 1
          }}
        >
          <Table
            size={isXs ? "small" : "medium"}
            sx={{
              '& .MuiTableCell-root': {
                borderColor: theme.palette.divider
              },
              '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
                borderBottom: 0
              },
              '& .MuiTableHead-root .MuiTableCell-root': {
                backgroundColor: '#f8f9fa',
                fontWeight: 'bold',
                borderBottom: `2px solid ${theme.palette.primary.main}`
              }
            }}
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                {hasPlaceOrResult && (
                  <TableCell 
                    align="center" 
                    sx={{ 
                      fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Lugar
                  </TableCell>
                )}
                {!hasPlaceOrResult && (
                  <TableCell 
                    align="center" 
                    sx={{ 
                      fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                      whiteSpace: 'nowrap' 
                    }}
                  >
                    Línea
                  </TableCell>
                )}
                {!hasPlaceOrResult && (
                  <TableCell 
                    align="center" 
                    sx={{ 
                      fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                      whiteSpace: 'nowrap' 
                    }}
                  >
                    Número
                  </TableCell>
                )}
                {hasYear && (
                  <TableCell 
                    align="center" 
                    sx={{ 
                      fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                      fontWeight: 'bold',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Yr
                  </TableCell>
                )}
                <TableCell 
                  sx={{ 
                    fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                    fontWeight: hasPlaceOrResult ? 'bold' : 'normal',
                  }}
                >
                  Nombre y Equipo
                </TableCell>
                {hasPlaceOrResult && (
                  <>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Resultado
                    </TableCell>
                    {hasScores && (
                      <TableCell 
                        align="center" 
                        sx={{ 
                          fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Puntaje
                      </TableCell>
                    )}
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <EventHeatRow 
                  key={r.number} 
                  {...r}
                  hasPlaceOrResult={hasPlaceOrResult}
                  showHeat={false}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CustomAccordion>
    );
  });

  /* ───── Tabla global de resultados ───── */
  const globalTable = globalResults.hasAnyResults ? (
    <CustomAccordion
      key="global_results"
      defaultExpanded
      headerComponent={
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography sx={{ 
              fontSize: getFontSize(16, 1, isXs, isSm, isMd),
              fontWeight: 'medium'
            }}>
              Resultados generales
            </Typography>
            <Chip 
              label={`${globalResults.rows.length} atletas con resultados`}
              size="small"
              variant="filled"
              sx={{ 
                ml: 1, 
                fontSize: getFontSize(12, 1, isXs, isSm, isMd),
                height: 22,
                fontWeight: 'medium',
                borderRadius: '12px',
                backgroundColor: openAccordions.has('global_results') 
                  ? '#4caf50' 
                  : '#ffffff',
                color: openAccordions.has('global_results') 
                  ? 'white' 
                  : theme.palette.primary.main,
                border: `1px solid ${theme.palette.primary.main}`,
                transition: 'all 0.3s ease-in-out',
                boxShadow: openAccordions.has('global_results') 
                  ? '0 2px 8px rgba(76, 175, 80, 0.3)' 
                  : '0 1px 3px rgba(0,0,0,0.1)',
              }}
            />
            {filterTeam && (
              <Chip 
                label={`Equipo: ${filterTeam}`}
                size="small"
                color="primary"
                onDelete={() => setFilterTeam('')}
                sx={{ 
                  ml: 1, 
                  fontSize: getFontSize(12, 1, isXs, isSm, isMd),
                  height: 22,
                  borderRadius: '12px'
                }}
              />
            )}
          </Box>
          {uniqueTeams.length > 1 && (
            <Box>
              <Tooltip title="Filtrar por equipo">
                <IconButton 
                  size="small"
                  onClick={(e) => {
                    // Crear menú de filtros (misma lógica que el original)
                    const menu = document.createElement('div');
                    menu.style.position = 'absolute';
                    menu.style.top = `${e.clientY}px`;
                    menu.style.left = `${e.clientX}px`;
                    menu.style.zIndex = '1000';
                    menu.style.backgroundColor = 'white';
                    menu.style.boxShadow = '0 3px 6px rgba(0,0,0,0.2)';
                    menu.style.borderRadius = '4px';
                    menu.style.padding = '8px';
                    
                    const ul = document.createElement('ul');
                    ul.style.listStyle = 'none';
                    ul.style.padding = '0';
                    ul.style.margin = '0';
                    ul.style.maxHeight = '300px';
                    ul.style.overflowY = 'auto';
                    
                    uniqueTeams.forEach(team => {
                      const li = document.createElement('li');
                      li.textContent = team;
                      li.style.padding = '8px 16px';
                      li.style.cursor = 'pointer';
                      li.style.borderRadius = '4px';
                      li.style.transition = 'background-color 0.2s';
                      li.onmouseover = () => {
                        li.style.backgroundColor = '#f0f7ff';
                      };
                      li.onmouseout = () => {
                        li.style.backgroundColor = 'transparent';
                      };
                      li.onclick = () => {
                        setFilterTeam(team);
                        document.body.removeChild(menu);
                      };
                      ul.appendChild(li);
                    });
                    
                    if (filterTeam) {
                      const clearLi = document.createElement('li');
                      clearLi.textContent = "Borrar filtro";
                      clearLi.style.padding = '8px 16px';
                      clearLi.style.cursor = 'pointer';
                      clearLi.style.borderRadius = '4px';
                      clearLi.style.color = '#f44336';
                      clearLi.style.fontWeight = 'bold';
                      clearLi.style.borderTop = '1px solid #eee';
                      clearLi.style.marginTop = '8px';
                      clearLi.onmouseover = () => {
                        clearLi.style.backgroundColor = '#fff0f0';
                      };
                      clearLi.onmouseout = () => {
                        clearLi.style.backgroundColor = 'transparent';
                      };
                      clearLi.onclick = () => {
                        setFilterTeam('');
                        document.body.removeChild(menu);
                      };
                      ul.appendChild(clearLi);
                    }
                    
                    menu.appendChild(ul);
                    document.body.appendChild(menu);
                    
                    const closeMenu = (evt) => {
                      if (!menu.contains(evt.target)) {
                        document.body.removeChild(menu);
                        document.removeEventListener('click', closeMenu);
                      }
                    };
                    
                    setTimeout(() => {
                      document.addEventListener('click', closeMenu);
                    }, 100);
                  }}
                  color="primary"
                >
                  <FilterListIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      }
      onChange={(isExpanded) => handleAccordionToggle('global_results', isExpanded)}
    >
      <TableContainer 
        component={Paper}
        elevation={0}
        variant="outlined"
        sx={{ 
          overflow: 'auto',
          borderRadius: 1
        }}
      >
        <Table 
          size={isXs ? "small" : "medium"}
          sx={{
            '& .MuiTableCell-root': {
              borderColor: theme.palette.divider
            },
            '& .MuiTableRow-root:last-of-type .MuiTableCell-root': {
              borderBottom: 0
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              backgroundColor: '#f8f9fa',
              fontWeight: 'bold',
              borderBottom: `2px solid ${theme.palette.primary.main}`
            }
          }}
        >
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell 
                align="center" 
                sx={{ 
                  fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}
              >
                Lugar
              </TableCell>
              {globalResults.hasYear && (
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Yr
                </TableCell>
              )}
              <TableCell 
                sx={{ 
                  fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                  fontWeight: 'bold',
                }}
              >
                Nombre y Equipo
              </TableCell>
              <TableCell 
                align="center" 
                sx={{ 
                  fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}
              >
                Resultado
              </TableCell>
              <TableCell 
                align="center" 
                sx={{ 
                  fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}
              >
                Heat
              </TableCell>
              {globalResults.hasScores && (
                <TableCell 
                  align="center" 
                  sx={{ 
                    fontSize: getFontSize(14, 1, isXs, isSm, isMd),
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Puntaje
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              [...Array(5)].map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  <TableCell>
                    <Skeleton variant="text" width="100%" height={40} />
                  </TableCell>
                  {globalResults.hasYear && (
                    <TableCell>
                      <Skeleton variant="text" width="100%" height={40} />
                    </TableCell>
                  )}
                  <TableCell>
                    <Skeleton variant="text" width="100%" height={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="100%" height={40} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="100%" height={40} />
                  </TableCell>
                  {globalResults.hasScores && (
                    <TableCell>
                      <Skeleton variant="text" width="100%" height={40} />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              filteredGlobalRows.map(r => (
                <EventHeatRow 
                  key={`g_${r.number}`} 
                  {...r}
                  hasPlaceOrResult={true}
                  showHeat={true}
                />
              ))
            )}
            {!loading && filteredGlobalRows.length === 0 && (
              <TableRow>
                <TableCell 
                  colSpan={globalResults.hasScores ? (globalResults.hasYear ? 6 : 5) : (globalResults.hasYear ? 5 : 4)}
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {globalResults.hasAnyResults 
                      ? "No hay resultados que mostrar con el filtro seleccionado."
                      : "No hay resultados disponibles aún."
                    }
                  </Typography>
                  {filterTeam && globalResults.hasAnyResults && (
                    <Button 
                      variant="outlined" 
                      size="small"
                      startIcon={<FilterListIcon />}
                      onClick={() => setFilterTeam('')}
                      sx={{ mt: 1 }}
                    >
                      Borrar filtro
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </CustomAccordion>
  ) : null;

  /* ═════════════ RENDER PRINCIPAL ═════════════ */
  return (
    <Box sx={{ 
      p: isXs ? 1 : 2,
      maxWidth: '100%',
      backgroundColor: theme.palette.background.default,
      borderRadius: 2,
      background: `linear-gradient(135deg, 
        ${theme.palette.background.default} 0%, 
        ${theme.palette.background.paper} 100%)`,
    }}>
      {/* Encabezado del evento */}
      <EventHeader 
        eventName={eventDetails[0].eventName}
        eventNumber={eventNumber}
        round={round}
        genderLabel={genderLabel}
        totalParts={totalParts}
        hasAnyResults={globalResults.hasAnyResults}
      />

      {/* Lista de heats */}
      <Box sx={{ mb: 3 }}>
        {/* tabla global primero, luego las de cada heat - SOLO si hay resultados */}
        {eventDetails.length > 1 && globalResults.hasAnyResults && globalTable}
        {heatsTables}
      </Box>
      
      {/* Leyenda de abreviaturas */}
      <AbbreviationsLegend />

      {/* Modal de Fotofinish */}
      <PhotofinishModal 
        photofinishModal={photofinishModal}
        closePhotofinishModal={closePhotofinishModal}
        eventName={eventDetails[0]?.eventName}
      />
    </Box>
  );
}